import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2, HelpCircle, Scale, CalendarDays, FileText } from "lucide-react";

const CONFIRMED = [
  "Internet and telephone voting are authorized for the City of Kawartha Lakes 2026 municipal election.",
  "By-law No. 2025-136 authorizing the voting method was passed on November 18, 2025.",
  "The Clerk's election procedures identify Simply Voting Inc. as the voting system provider.",
  "The voting period runs from October 16, 2026 at 9:00 a.m. to October 26, 2026 at 8:00 p.m.",
  "The City has stated that paper ballots will not be available.",
  "The Clerk's procedures provide for voting system testing and for an auditor appointed by the Clerk.",
  "A third-party web-application security assessment / penetration test of the voting system is provided for.",
  "The official voting system records receipt codes and voter participation, but does not record which candidate a voter chose in a way that is available for public verification.",
];

const CONCERNS = [
  "Electors cannot independently confirm, after the fact, that their recorded vote matched their intent — a limitation security researchers commonly identify in online voting systems.",
  "Some vendor and system records may be withheld or redacted under the Municipal Freedom of Information and Protection of Privacy Act (MFIPPA), including records supplied in confidence or treated by a vendor as trade secrets or confidential commercial information. Confidentiality of this kind is lawful and common — and it also means the public cannot inspect every technical detail of the system.",
  "Without paper ballots there is no physical artifact to support a conventional recount.",
];

const REMEDIES = [
  "Voluntary, independent witnessing of votes (this platform) to establish a documented minimum for participating candidates — comparable with official results after the close of voting.",
  "Public release, to the maximum lawful extent, of testing, audit, and security-assessment documentation.",
  "Consideration of paper ballots or voter-verifiable records in future elections.",
  "Where any apparent discrepancy arises, review strictly through the proper legal and election processes.",
];

export default function KawarthaLakes2026() {
  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">Information page</Badge>
          <Badge variant="outline">Facts with sources · no allegations</Badge>
        </div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="kl2026-title">
          Kawartha Lakes 2026 municipal election
        </h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">
          This page separates <strong>confirmed facts</strong>, <strong>concerns</strong>, and{" "}
          <strong>requested remedies</strong>. Witness Your Vote does not claim that any confidentiality proves
          wrongdoing, and it does not declare fraud. It documents what is publicly known and what independent
          witnessing adds.
        </p>
      </div>

      <div className="mt-10 max-w-3xl space-y-8">
        <Card>
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2.5 mb-5">
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              <h2 className="font-heading font-semibold text-xl">Confirmed facts</h2>
            </div>
            <ul className="space-y-3">
              {CONFIRMED.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                  <span className="font-mono text-xs text-slate-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground mt-5">
              Sources: City of Kawartha Lakes By-law No. 2025-136 (passed November 18, 2025); the City Clerk's 2026
              election procedures.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2.5 mb-5">
              <HelpCircle className="h-5 w-5 text-amber-600" />
              <h2 className="font-heading font-semibold text-xl">Concerns</h2>
            </div>
            <ul className="space-y-3">
              {CONCERNS.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                  <span className="font-mono text-xs text-slate-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {f}
                </li>
              ))}
            </ul>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 mt-5">
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Balanced research context:</strong> research commissioned by
                  Elections Canada on online voting notes both potential benefits — accessibility and convenience for
                  many electors — and ongoing challenges identified by security researchers, including verifiability,
                  auditability, and the difficulty of publicly demonstrating that electronic counts are correct. That
                  research does not conclude that any particular system is compromised, and neither does this platform.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2.5 mb-5">
              <Scale className="h-5 w-5 text-primary" />
              <h2 className="font-heading font-semibold text-xl">Requested remedies</h2>
            </div>
            <ul className="space-y-3">
              {REMEDIES.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                  <span className="font-mono text-xs text-slate-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {f}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-secondary">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <CalendarDays className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-heading font-semibold text-sm">Voting: October 16 (9:00 a.m.) – October 26 (8:00 p.m.), 2026</p>
                <p className="text-xs text-muted-foreground mt-1">After you vote, you can witness your vote here for a participating candidate.</p>
              </div>
            </div>
            <Button asChild data-testid="kl2026-find-candidate">
              <Link to="/find-a-candidate">Find my candidate</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
